/**
 * Concurrency-limited fetch wrapper for Supabase edge function calls.
 *
 * Prevents burst storms that trigger Supabase's 503 routing-layer throttle by
 * limiting the number of in-flight requests from a single browser tab.
 *
 * Features:
 *  - Semaphore with configurable max concurrency (default 4)
 *  - Priority support: `{ priority: 'high' }` bypasses the queue (for chat-complete)
 *  - AbortSignal forwarding: pass `{ signal }` to cancel requests on navigation
 *  - Automatic retry on 503 with exponential backoff (configurable)
 */

const DEFAULT_MAX_CONCURRENT = 4;
const DEFAULT_MAX_RETRIES = 2;
const DEFAULT_RETRY_BASE_MS = 1000;

let inFlight = 0;
const queue = []; // { resolve, args }

function drain() {
  while (queue.length > 0 && inFlight < DEFAULT_MAX_CONCURRENT) {
    const next = queue.shift();
    next.resolve();
  }
}

function acquireSlot() {
  if (inFlight < DEFAULT_MAX_CONCURRENT) {
    inFlight++;
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    queue.push({ resolve });
  });
}

function releaseSlot() {
  inFlight--;
  drain();
}

/**
 * @param {string} url
 * @param {RequestInit & { priority?: 'high', maxRetries?: number, retryBaseMs?: number }} options
 * @returns {Promise<Response>}
 */
export async function edgeFetch(url, options = {}) {
  const {
    priority,
    maxRetries = DEFAULT_MAX_RETRIES,
    retryBaseMs = DEFAULT_RETRY_BASE_MS,
    ...fetchOptions
  } = options;

  const isHighPriority = priority === 'high';

  if (!isHighPriority) {
    await acquireSlot();
  } else {
    inFlight++;
  }

  try {
    return await fetchWithRetry(url, fetchOptions, maxRetries, retryBaseMs);
  } finally {
    if (!isHighPriority) {
      releaseSlot();
    } else {
      inFlight--;
      drain();
    }
  }
}

async function fetchWithRetry(url, fetchOptions, maxRetries, retryBaseMs) {
  let lastError;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (fetchOptions.signal?.aborted) {
      throw new DOMException('The operation was aborted.', 'AbortError');
    }
    try {
      const res = await fetch(url, fetchOptions);
      if (res.status === 503 && attempt < maxRetries) {
        const delay = retryBaseMs * Math.pow(2, attempt);
        await sleep(delay, fetchOptions.signal);
        continue;
      }
      return res;
    } catch (err) {
      if (err.name === 'AbortError') throw err;
      lastError = err;
      if (attempt < maxRetries) {
        const delay = retryBaseMs * Math.pow(2, attempt);
        await sleep(delay, fetchOptions.signal);
        continue;
      }
    }
  }
  throw lastError;
}

function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('The operation was aborted.', 'AbortError'));
      return;
    }
    const timer = setTimeout(resolve, ms);
    if (signal) {
      const onAbort = () => {
        clearTimeout(timer);
        reject(new DOMException('The operation was aborted.', 'AbortError'));
      };
      signal.addEventListener('abort', onAbort, { once: true });
    }
  });
}

/**
 * Create a scope that tracks an AbortController.
 * Call `.abort()` to cancel all pending fetches tied to this scope.
 * Call `.renew()` to abort the old controller and create a fresh one.
 */
export function createAbortScope() {
  let controller = new AbortController();
  return {
    get signal() { return controller.signal; },
    abort() { controller.abort(); },
    renew() {
      controller.abort();
      controller = new AbortController();
      return controller.signal;
    },
  };
}
